import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import type { LeagueLaunch as LeagueLaunchType } from '@sorare/marketplace/src/lib/newSeason';

import { ExpandedLeagueLaunch } from '#src/components/new-season/ExpandedLeagueLaunch/index.tsx';

import type {
  ReleasedLeagues_competition,
  ReleasedLeagues_so5Competition,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 4 })`
  content-visibility: auto;
  contain-intrinsic-block-size: 20px;
`;

type Props = {
  releasedLeagues: LeagueLaunchType<
    ReleasedLeagues_competition,
    ReleasedLeagues_so5Competition
  >[];
  registerLeagueRef: (slug: string, ref: HTMLDivElement | null) => void;
};

export const ReleasedLeagues = ({
  releasedLeagues,
  registerLeagueRef,
}: Props) => {
  if (releasedLeagues.length === 0) return null;

  return (
    <Root>
      <BrandL className="text-center">
        <FormattedMessage defaultMessage="Released" />
      </BrandL>
      {releasedLeagues.map(league => (
        <div
          key={league.slug}
          ref={element => registerLeagueRef(league.slug, element)}
        >
          <ExpandedLeagueLaunch leagueLaunch={league} />
        </div>
      ))}
    </Root>
  );
};

ReleasedLeagues.fragments = {
  competition: gql`
    fragment ReleasedLeagues_competition on Competition {
      slug
      ...ExpandedLeagueLaunch_competition
    }
    ${ExpandedLeagueLaunch.fragments.competition}
  ` as TypedDocumentNode<ReleasedLeagues_competition>,
  so5Competition: gql`
    fragment ReleasedLeagues_so5Competition on So5Competition {
      slug
      ...ExpandedLeagueLaunch_so5Competition
    }
    ${ExpandedLeagueLaunch.fragments.so5Competition}
  ` as TypedDocumentNode<ReleasedLeagues_so5Competition>,
};
